import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, Loader, Check, X as XIcon, RefreshCw } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';

const TIER_BADGE = { write: 'sf-badge-manager', send: 'sf-badge-warning', money: 'sf-badge-warning', destructive: 'sf-badge-error' };
const STATUS_BADGE = { pending: 'sf-badge-warning', executed: 'sf-badge-success', approved: 'sf-badge-success', failed: 'sf-badge-error', rejected: 'sf-badge-neutral', expired: 'sf-badge-neutral' };
const FILTERS = ['pending', 'executed', 'rejected', 'failed', 'expired', 'all'];

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function AgentApprovalsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/support/approvals', { params: { status } });
      setItems(res.data.data || []);
    } catch (err) {
      alertService.error(err.response?.data?.error || 'Could not load approvals');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // A new request shows up without a refresh.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    const socket = io('/inbox', { auth: { token }, transports: ['websocket'], reconnectionAttempts: 5 });
    socket.on('agent:approval_requested', () => load());
    return () => socket.disconnect();
  }, [load]);

  const decide = async (item, action) => {
    let reason = '';
    if (action === 'reject') {
      reason = window.prompt('Reason shown to the customer (optional)') || '';
    } else if (!(await alertService.confirm('Approve this action?', `Run this ${item.tier} action now?\n\n${item.summary}`, item.tier === 'write' ? 'warning' : 'danger', 'Approve and run'))) {
      return;
    }
    setBusyId(item._id);
    try {
      await api.post(`/support/approvals/${item._id}/${action}`, action === 'reject' ? { reason } : {});
      alertService.success(action === 'approve' ? 'Approved and run' : 'Rejected');
      await load();
    } catch (err) {
      alertService.error(err.response?.data?.error || 'Could not complete that');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Agent approvals"
        subtitle="Actions your AI agents asked a person to approve before running"
        icon={ShieldCheck}
        actions={(
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#444444] bg-[#F3F2F2] hover:bg-[#E5E5E5] px-3 py-2 rounded">
            <RefreshCw size={14} /> Refresh
          </button>
        )}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setStatus(f)} className={`text-[12px] px-3 py-1.5 rounded-full border ${status === f ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#444444] border-[#D8DDE6]'}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="sf-card overflow-x-auto">
        {loading ? (
          <div className="p-8 flex justify-center"><Loader className="animate-spin text-[#0176D3]" size={20} /></div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-[13px] text-[#706E6B]">Nothing here. When an agent wants to do something above its auto-approve level, it appears in this list.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] text-[#706E6B] border-b border-[#E5E5E5]">
                <th className="p-3">Action</th><th className="p-3">Risk</th><th className="p-3">Channel</th><th className="p-3">Asked</th><th className="p-3">Status</th><th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it._id} className="border-b border-[#F3F2F2] align-top">
                  <td className="p-3">
                    <div className="font-mono text-[12px] break-all">{it.summary}</div>
                    {it.error && <div className="text-[12px] text-[#8E030F] mt-1">{it.error}</div>}
                    {it.rejectReason && <div className="text-[12px] text-[#706E6B] mt-1">Reason: {it.rejectReason}</div>}
                  </td>
                  <td className="p-3"><span className={`sf-badge ${TIER_BADGE[it.tier] || 'sf-badge-neutral'}`}>{it.tier}</span></td>
                  <td className="p-3 capitalize">{it.channel}</td>
                  <td className="p-3 whitespace-nowrap">{fmt(it.createdAt)}</td>
                  <td className="p-3"><span className={`sf-badge ${STATUS_BADGE[it.status] || 'sf-badge-neutral'}`}>{it.status}</span></td>
                  <td className="p-3">
                    {it.status === 'pending' && (
                      <div className="flex gap-2">
                        <button type="button" disabled={busyId === it._id} onClick={() => decide(it, 'approve')} className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 px-3 py-1.5 rounded">
                          <Check size={13} /> Approve
                        </button>
                        <button type="button" disabled={busyId === it._id} onClick={() => decide(it, 'reject')} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#444444] bg-[#F3F2F2] hover:bg-[#E5E5E5] disabled:opacity-50 px-3 py-1.5 rounded">
                          <XIcon size={13} /> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
